import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ModalService } from '@rocketfinancialcorp/rocket-ui/modal';
import { DatatableColumn, DatatableSorting, FetchData, RktTableComponent } from '@rocketfinancialcorp/rocket-ui/table';
import { FilterMenuComponent } from '@shared/components';
import { FilterField, FilterValues, RequestPageParams } from '@shared/models';
import { transactionFeature, TransactionsActions } from '@store';
import { identity, isUndefined, pickBy } from 'lodash-es';

@Component({
  selector: 'app-ledger-entries',
  standalone: true,
  imports: [AsyncPipe, RktIconComponent, FilterMenuComponent, RktTableComponent],
  templateUrl: './ledger-entries.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LedgerEntriesComponent {
  store = inject(Store);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  modalService = inject(ModalService);

  transactionId = input<string>();

  businessAccountId = input<string>();

  financialAccountId = input<string>();

  columns: DatatableColumn[] = [
    { name: 'Created At', prop: 'createdAt', fixedSize: 150, colType: 'date', isSortable: true },
    { name: 'FA ID', prop: 'financialAccountId', flexSize: 173, colType: 'short-id', isSortable: true },
    { name: 'Description', prop: 'description', colType: 'two-line-col', flexSize: 450 },
    { name: 'Entry Type', prop: 'entryType', flexSize: 200, colAlign: 'right' },
    { name: 'Amount', prop: 'amount', flexSize: 200, colType: 'amount', colAlign: 'right' },
    { name: 'Account Balance', prop: 'accountBalance', flexSize: 200, colType: 'amount', colAlign: 'right' },
    { name: 'Currency', prop: 'currency', flexSize: 200, colAlign: 'right' },
    { name: 'Status', prop: 'status', fixedSize: 140, colType: 'status-tag', colAlign: 'right' },
  ];

  filters: FilterField[] = [
    {
      name: 'date',
      displayName: 'Date',
      type: 'DATE',
      options: [
        { label: 'Today', value: 'TODAY' },
        { label: 'Yesterday', value: 'YESTERDAY' },
        { label: 'This week', value: 'THIS_WEEK' },
        { label: 'This month', value: 'THIS_MONTH' },
        { label: '90 days', value: 'DEFAULT' },
        { label: 'Custom range', value: 'CUSTOM' },
      ],
      defaultValue: 'DEFAULT',
      props: {
        maxMonths: '15',
      },
    },
    {
      name: 'entryType',
      displayName: 'Amount Type',
      type: 'RADIO',
      options: [
        { label: 'Credits', value: 'CREDIT' },
        { label: 'Debits', value: 'DEBIT' },
        { label: 'OPENING BALANCE', value: 'OPENING_BALANCE' },
      ],
    },
    {
      name: 'amount',
      displayName: 'Amount',
      type: 'AMOUNT',
    },
    {
      name: 'transactionStatus',
      displayName: 'Status',
      type: 'RADIO',
      options: [
        { label: 'All', value: undefined },
        { label: 'Approved', value: 'APPROVED' },
        { label: 'Cancelled', value: 'CANCELLED' },
        { label: 'Cleared', value: 'CLEARED' },
        { label: 'Declined', value: 'DECLINED' },
        { label: 'Error', value: 'ERROR' },
        { label: 'New', value: 'NEW' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'Processing', value: 'PROCESSING' },
        { label: 'Settled', value: 'SETTLED' },
        { label: 'Reversed', value: 'REVERSED' },
      ],
    },
  ];

  sortParams: DatatableSorting = { key: 'createdAt', sortProp: 'createdAt', sortDir: 'desc' };

  get activePage(): number {
    const routePage = this.activatedRoute?.snapshot?.queryParams?.page;
    return routePage ? parseInt(routePage, 10) : 0;
  }

  ledgerEntries$ = this.store.select(transactionFeature.selectLedgerEntries);

  totalElements$ = this.store.select(transactionFeature.selectLedgerEntriesTotalElements);

  isLoading$ = this.store.select(transactionFeature.selectLedgerEntriesLoading);

  activeFilters$ = this.store.select(transactionFeature.selectLedgerEntriesActiveFilters);

  activeFiltersCount$ = this.store.select(transactionFeature.selectLedgerEntriesActiveFiltersCount);

  updateQueryParams(page?: RequestPageParams['page']): void {
    if (!isUndefined(page) && this.activePage !== page) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { page: page > 0 ? page : undefined },
        queryParamsHandling: 'merge',
      });
    }
  }

  getLedgerEntries({ page, size, sortParams }: FetchData): void {
    if (sortParams) {
      this.sortParams = sortParams;
    }

    this.updateQueryParams(page);

    this.store.dispatch(
      TransactionsActions.loadLedgerEntries({
        params: {
          page: page ?? 0,
          size,
          sortParams: sortParams ?? this.sortParams,
        },
      }),
    );
  }

  applyFilters(filterValues: FilterValues): void {
    this.store.dispatch(TransactionsActions.setLedgerEntriesActiveFilters({ filters: { ...pickBy(filterValues, identity) } }));
    this.getLedgerEntries({ page: 0 });
  }

  transactionDetailsUrl = computed(() => {
    if (this.financialAccountId()) {
      return `/app/financial-accounts/${this.financialAccountId()}/activities/${this.transactionId()}/details`;
    }
    if (this.businessAccountId()) {
      return `/app/business-accounts/${this.businessAccountId()}/transactions/${this.transactionId()}/details`;
    }
    return `/app/transactions/${this.transactionId()}/details`;
  });

  onActivityRowClick(financialAccountId?: string): void {
    if (!financialAccountId) return;
    this.router.navigateByUrl(`/app/financial-accounts/${financialAccountId}`);
  }
}
